import { useState, useEffect } from 'react';
import axios from 'axios';
import { Navbar, Footer } from '../components/Layout';
import { useTranslation } from '../context/TranslationContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Trophy, Medal, Star, Crown } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const LeaderboardPage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, tFormat } = useTranslation();

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await axios.get(`${API_URL}/leaderboard`);
        setLeaderboard(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <Trophy className="w-6 h-6 text-amber-500" />;
      case 2: return <Medal className="w-6 h-6 text-gray-400" />;
      case 3: return <Medal className="w-6 h-6 text-amber-700" />;
      default: return <span className="text-lg font-bold text-muted-foreground">{rank}</span>;
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1: return 'bg-amber-500/10 border-amber-500/30';
      case 2: return 'bg-gray-500/10 border-gray-500/30';
      case 3: return 'bg-amber-700/10 border-amber-700/30';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="leaderboard-page">
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-4xl font-serif font-medium mb-2">
              {t('lb_title', 'Leaderboard')}
            </h1>
            <p className="text-muted-foreground">{t('lb_subtitle', 'Top learners on THACHARI')}</p>
          </div>

          {/* Leaderboard */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : leaderboard?.leaders?.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Star className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {t('lb_empty_title', 'No learners yet')}
                </h3>
                <p className="text-muted-foreground">
                  {t('lb_empty_hint', 'Be the first to earn points!')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {leaderboard?.leaders?.map((user) => (
                <Card
                  key={user.id}
                  className={`transition-all hover:shadow-md ${getRankStyle(user.rank)}`}
                  data-testid={`leaderboard-user-${user.rank}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-muted">
                      {getRankIcon(user.rank)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{user.name}</h3>
                        {user.badges?.length > 0 && (
                          <div className="flex gap-1">
                            {user.badges.slice(0, 2).map((badge) => (
                              <Badge key={badge} variant="secondary" className="text-xs">
                                {badge.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {tFormat(
                          'lb_badges_line',
                          { n: user.badges?.length || 0 },
                          '{n} badges earned'
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Star className="w-4 h-4 text-amber-500" />
                        <span className="font-bold text-lg">{user.points}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t('lb_points', 'points')}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* How to earn points */}
          <Card className="mt-12">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4">
                {t('lb_how_title', 'How to earn points')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500">+10</Badge>
                  <span>{t('lb_earn_lesson', 'Complete a lesson')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-500">+20</Badge>
                  <span>{t('lb_earn_quiz', 'Pass a quiz')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-purple-500">+100</Badge>
                  <span>{t('lb_earn_course', 'Complete a course')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LeaderboardPage;
